import React, { useEffect, useRef, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import MembershipTracking from "../components/MembershipTracking";
import api from "../api/axiosInstance";
import {
  Flag,
  Users,
  Award,
  Tag,
  Star,
  Shield,
  Clock,
  CheckCircle,
  Sparkles,
  ArrowRight,
  Crown,
  Gem,
  Trophy,
  Coffee,
  MapPin,
  Phone,
  Mail,
  Zap,
  Target,
} from "lucide-react";
import "./Membership.css"; // Import custom CSS for additional styling

// Add missing Globe and Building icons
const Globe = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
);

const Building = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
  </svg>
);

const memberships = [
  {
    title: "MILITARY",
    description: "Both for Serving & Retired officers",
    price: "৳ 500",
    monthlyPrice: "৳ 500/month",
    totalFee: "৳ 5,000",
    bg: "from-blue-50 to-indigo-50",
    hoverBg: "hover:from-blue-100 hover:to-indigo-100",
    border: "border-blue-100",
    icon: <Shield className="w-6 h-6" />,
    badge: "Military Exclusive",
    badgeColor: "bg-gradient-to-r from-blue-500 to-indigo-500",
    features: ["Exclusive events", "Priority booking", "Family access"],
    highlight: false,
  },
  {
    title: "SERVING GOVERNMENT OFFICERS",
    description: "For all serving civil government officers",
    price: "৳ 800",
    monthlyPrice: "৳ 800/month",
    totalFee: "৳ 100,000",
    bg: "from-amber-50 to-orange-50",
    hoverBg: "hover:from-amber-100 hover:to-orange-100",
    border: "border-amber-100",
    icon: <Award className="w-6 h-6" />,
    badge: "Government Officers",
    badgeColor: "bg-gradient-to-r from-amber-500 to-orange-500",
    features: ["50% discount", "Special events", "VIP services"],
    highlight: false,
  },
  {
    title: "CIVILIANS",
    description: "Any other civilian person",
    price: "৳ 1,200",
    monthlyPrice: "৳ 1,200/month",
    totalFee: "৳ 250,000",
    bg: "from-emerald-50 to-teal-50",
    hoverBg: "hover:from-emerald-100 hover:to-teal-100",
    border: "border-emerald-100",
    icon: <Users className="w-6 h-6" />,
    badge: "Premium Access",
    badgeColor: "bg-gradient-to-r from-emerald-500 to-teal-500",
    features: ["Full facilities", "Guest passes", "Pro shop discount"],
    highlight: false,
  },
  {
    title: "FOREIGNERS/DIPLOMATS",
    description: "For international members",
    price: "৳ 2,000",
    monthlyPrice: "৳ 2,000/month",
    totalFee: "৳ 600,000",
    bg: "from-purple-50 to-pink-50",
    hoverBg: "hover:from-purple-100 hover:to-pink-100",
    border: "border-purple-100",
    icon: <Globe className="w-6 h-6" />,
    badge: "International",
    badgeColor: "bg-gradient-to-r from-purple-500 to-pink-500",
    features: ["Multi-language support", "Travel assistance", "Global network"],
    highlight: false,
  },
  {
    title: "NON-GOVERNMENT ORGANIZATIONS/AGENCY",
    description: "For corporate organizations",
    price: "৳ 1,500",
    monthlyPrice: "৳ 1,500/month",
    totalFee: "৳ 150,000",
    bg: "from-red-50 to-rose-50",
    hoverBg: "hover:from-red-100 hover:to-rose-100",
    border: "border-red-100",
    icon: <Building className="w-6 h-6" />,
    badge: "Business Network",
    badgeColor: "bg-gradient-to-r from-red-500 to-rose-500",
    features: ["Team events", "Meeting facilities", "Brand visibility"],
    highlight: false,
  },
];

const benefits = [
  {
    icon: <Flag className="w-8 h-8" />,
    title: "Unlimited Golf Access",
    description: "Full access to championship 9-hole course",
    color: "from-emerald-500 to-lime-500",
    bg: "bg-gradient-to-br from-emerald-50 to-lime-50",
  },
  {
    icon: <Coffee className="w-8 h-8" />,
    title: "Fine Dining",
    description: "Exclusive restaurants and bars",
    color: "from-amber-500 to-orange-500",
    bg: "bg-gradient-to-br from-amber-50 to-orange-50",
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Networking Events",
    description: "Member-only social and business events",
    color: "from-blue-500 to-indigo-500",
    bg: "bg-gradient-to-br from-blue-50 to-indigo-50",
  },
  {
    icon: <Star className="w-8 h-8" />,
    title: "Pro Shop Discounts",
    description: "Special pricing on premium equipment",
    color: "from-purple-500 to-pink-500",
    bg: "bg-gradient-to-br from-purple-50 to-pink-50",
  },
  {
    icon: <Trophy className="w-8 h-8" />,
    title: "Tournament Access",
    description: "Entry to exclusive golf tournaments",
    color: "from-yellow-500 to-amber-500",
    bg: "bg-gradient-to-br from-yellow-50 to-amber-50",
  },
  {
    icon: <Shield className="w-8 h-8" />,
    title: "Priority Services",
    description: "VIP treatment and priority booking",
    color: "from-red-500 to-rose-500",
    bg: "bg-gradient-to-br from-red-50 to-rose-50",
  },
];

// Maps the Category enum label the backend sends (com.rgcc.golfclub.Enum.Category)
// to the corresponding plan `title` in the `memberships` array above, so a special
// offer for a category can be matched to the right pricing card.
const CATEGORY_TO_PLAN_TITLE = {
  Military: "MILITARY",
  "Serving Govt Officer": "SERVING GOVERNMENT OFFICERS",
  Civilian: "CIVILIANS",
  "Foreigners/Diplomats": "FOREIGNERS/DIPLOMATS",
  "Non-government Organization/Agency": "NON-GOVERNMENT ORGANIZATIONS/AGENCY",
};

const formatOfferDate = (dateStr) => {
  if (!dateStr) return "";
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const Membership = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeOffers, setActiveOffers] = useState([]);

  const membershipRef = useRef(null);

  // Special offers are fully admin-controlled and can start/stop or expire at
  // any time, so they're fetched fresh on every page load rather than hardcoded.
  useEffect(() => {
    const fetchActiveOffers = async () => {
      try {
        const res = await api.get("/special-offers/active");
        setActiveOffers(res.data || []);
      } catch (err) {
        console.error("Failed to fetch special offers:", err);
        // Fail silently on this public page - plans just render without any offer.
      }
    };
    fetchActiveOffers();
  }, []);

  // Merge any active offer into its matching plan card (ribbon + discounted price).
  // A plan with no matching active offer renders exactly as it does by default.
  const displayMemberships = memberships.map((plan) => {
    const offer = activeOffers.find(
      (o) => CATEGORY_TO_PLAN_TITLE[o.category] === plan.title
    );
    if (!offer) {
      return plan;
    }
    return {
      ...plan,
      highlight: true,
      highlightText: offer.badgeText || "Limited Time Offer",
      deadline: formatOfferDate(offer.endDate),
      totalFee: offer.discountedPrice || plan.totalFee,
      originalFee: offer.originalPrice || plan.totalFee,
    };
  });

  useEffect(() => {
    setTimeout(() => {
      if (membershipRef.current) {
        membershipRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 1000);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-lime-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-emerald-900 via-emerald-800 to-lime-800 text-white py-16 md:py-20 overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-lime-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-lime-400 rounded-full blur-lg opacity-50"></div>
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-300/20 to-lime-300/20 backdrop-blur-sm border-2 border-emerald-300/30 flex items-center justify-center shadow-2xl">
                <Crown className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Join the{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Elite Circle</span>
              {/* <span className="absolute bottom-2 left-0 w-full h-3 bg-gradient-to-r from-emerald-400 to-lime-400 -rotate-1"></span> */}
            </span>{" "}
            at RGCC
          </h1>

          <p className="text-xl text-emerald-100 max-w-3xl mx-auto mb-8 leading-relaxed">
            Experience premium golfing, exclusive amenities, and a prestigious
            community at Rangpur Golf & Country Club. Your gateway to excellence
            begins here.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-emerald-200">
              <CheckCircle className="w-5 h-5" />
              <span>Premium Facilities</span>
            </div>
            <div className="w-1 h-1 bg-emerald-300 rounded-full"></div>
            <div className="flex items-center gap-2 text-emerald-200">
              <Star className="w-5 h-5" />
              <span>Exclusive Events</span>
            </div>
            <div className="w-1 h-1 bg-emerald-300 rounded-full"></div>
            <div className="flex items-center gap-2 text-emerald-200">
              <Shield className="w-5 h-5" />
              <span>VIP Services</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Tabs Navigation */}
        <div className="mb-12">
          <Tabs selectedIndex={activeTab} onSelect={setActiveTab}>
            <TabList className="flex flex-wrap gap-4 mb-8">
              <Tab className="focus:outline-none">
                <button
                  ref={membershipRef}
                  className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                    activeTab === 0
                      ? "bg-gradient-to-r from-emerald-500 to-lime-500 text-white shadow-xl transform -translate-y-1"
                      : "bg-white text-gray-700 hover:bg-gray-50 shadow-lg hover:shadow-xl border border-gray-100"
                  }`}
                >
                  <Flag className="w-5 h-5" />
                  Membership Plans
                </button>
              </Tab>
              <Tab className="focus:outline-none">
                <button
                  className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                    activeTab === 1
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-xl transform -translate-y-1"
                      : "bg-white text-gray-700 hover:bg-gray-50 shadow-lg hover:shadow-xl border border-gray-100"
                  }`}
                >
                  <Target className="w-5 h-5" />
                  Track Application
                </button>
              </Tab>
            </TabList>

            {/* Tab 1: Membership Plans */}
            <TabPanel>
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-10 mb-12 border border-emerald-100">
                <div className="flex items-center mb-10">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-100 to-lime-100 flex items-center justify-center mr-6">
                    <Gem className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      Exclusive Membership Plans
                    </h2>
                    <p className="text-gray-600 mt-2">
                      Choose the perfect plan for your profile and enjoy premium
                      benefits
                    </p>
                  </div>
                </div>

                {/* Membership Cards Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {displayMemberships.map((plan, index) => (
                    <div
                      key={index}
                      className={`group relative rounded-2xl p-8 border-2 transition-all duration-500 hover:shadow-2xl hover:scale-[1.02] ${plan.border} ${plan.bg} ${plan.hoverBg} overflow-hidden`}
                    >
                      {/* Special Offer Badge */}
                      {plan.highlight && (
                        <div className="absolute top-0 right-0 z-10">
                          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 text-sm font-bold shadow-xl transform translate-x-4 -translate-y-2 rotate-12">
                            <div className="flex items-center gap-2">
                              <Zap className="w-4 h-4" />
                              {plan.highlightText}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Icon and Badge */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
                          <div className="text-emerald-600">{plan.icon}</div>
                        </div>
                        <div
                          className={`px-4 py-1.5 rounded-full text-white text-sm font-bold ${plan.badgeColor}`}
                        >
                          {plan.badge}
                        </div>
                      </div>

                      {/* Title and Description */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {plan.title}
                      </h3>
                      <p className="text-gray-600 mb-6">{plan.description}</p>

                      {/* Price Section */}
                      <div className="mb-6 p-4 bg-white/50 rounded-xl border border-gray-100">
                        <div className="flex items-end gap-2 mb-2">
                          <span className="text-3xl font-bold text-gray-900">
                            {plan.price}
                          </span>
                          <span className="text-gray-500 mb-1">/month</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Total Membership Fee:{" "}
                          <span className="font-bold text-gray-900">
                            {plan.totalFee}
                          </span>
                          {plan.originalFee && (
                            <span className="ml-2 line-through text-gray-400">
                              {plan.originalFee}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Features */}
                      {/* <div className="mb-8">
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          Key Benefits
                        </h4>
                        <ul className="space-y-2">
                          {plan.features.map((feature, idx) => (
                            <li
                              key={idx}
                              className="flex items-center gap-2 text-sm text-gray-600"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div> */}

                      {/* Apply Button */}
                      {/* <a
                        href="/membership-registration"
                        className={`block w-full text-center py-3.5 rounded-xl font-bold transition-all duration-300 ${
                          plan.highlight
                            ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                            : "bg-gradient-to-r from-emerald-500 to-lime-500 text-white hover:from-emerald-600 hover:to-lime-600 shadow-lg hover:shadow-xl"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {plan.highlight ? "Get Special Offer" : "Apply Now"}
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </a> */}

                      <a
                        href={`/membership-registration?category=${encodeURIComponent(
                          plan.title,
                        )}`}
                        className={`block w-full text-center py-3.5 rounded-xl font-bold transition-all duration-300 ${
                          plan.highlight
                            ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                            : "bg-gradient-to-r from-emerald-500 to-lime-500 text-white hover:from-emerald-600 hover:to-lime-600 shadow-lg hover:shadow-xl"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {plan.highlight ? "Get Special Offer" : "Apply Now"}
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </a>
                      {/* Deadline Note for Highlight */}
                      {plan.highlight && (
                        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-amber-600 font-semibold">
                          <Clock className="w-4 h-4" />
                          Offer ends {plan.deadline}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Special Offer Banner(s) - one per currently active offer, admin-controlled.
                    Renders nothing at all when there's no active offer. */}
                {activeOffers.map((offer) => (
                  <div
                    key={offer.id}
                    className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-orange-500 shadow-lg"
                  >
                    <div className="flex flex-col md:flex-row items-center gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                          <Tag className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-2xl font-bold text-orange-700 mb-3">
                          {offer.title}
                        </h4>
                        <p className="text-gray-700 text-lg">
                          {offer.description}
                        </p>
                      </div>
                      <a
                        href={`/membership-registration?category=${encodeURIComponent(
                          CATEGORY_TO_PLAN_TITLE[offer.category] || ""
                        )}`}
                        className="flex-shrink-0 px-8 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                      >
                        {offer.ctaText || "Claim Offer"}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </TabPanel>

            {/* Tab 2: Track Application */}
            <TabPanel>
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-10 border border-emerald-100">
                <MembershipTracking />
              </div>
            </TabPanel>
          </Tabs>
        </div>

        {/* Membership Benefits Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-emerald-500" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Premium Member Benefits
              </h2>
              <Sparkles className="w-6 h-6 text-emerald-500" />
            </div>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Experience unparalleled privileges and exclusive amenities as part
              of the RGCC family
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`${benefit.bg} rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-500 group`}
              >
                <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <div
                    className={`bg-gradient-to-r ${benefit.color} bg-clip-text text-transparent`}
                  >
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-emerald-500 to-lime-500 rounded-2xl p-10 text-white text-center shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold mb-6">
              Ready to Join the Elite?
            </h3>
            <p className="text-emerald-100 text-lg mb-8">
              Have questions about membership? Our team is here to help you
              choose the perfect plan and guide you through the application
              process.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a
                href="tel:+8801769663142"
                className="flex items-center gap-3 px-8 py-3 bg-white text-emerald-600 rounded-xl font-bold hover:bg-emerald-50 transition-colors duration-300 shadow-lg"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <a
                href="mailto:rgcc66div@gmail.com"
                className="flex items-center gap-3 px-8 py-3 bg-emerald-700 text-white rounded-xl font-bold hover:bg-emerald-800 transition-colors duration-300 shadow-lg"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
              <a
                href="/contact"
                className="flex items-center gap-3 px-8 py-3 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-colors duration-300"
              >
                <MapPin className="w-5 h-5" />
                Visit Club
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Membership;
